import { Box, Stack, stackClasses, Typography } from '@mui/joy';
import { t } from 'i18next';

import { isSafeExternalUrl } from 'core/utils/url';
import { BellNotificationAction, BellNotificationSeverity } from 'notifications/enums';
import {
  IBellNotificationContent,
  IBellNotificationCta,
  IBellNotificationHandler,
  IBellNotificationInternalOrder,
  IBellNotificationReference,
  IExternalOrderUpdateNotification,
} from 'notifications/types';
import { toRouterPath } from 'notifications/utils/notification';
// By file: the quotes/elements barrel pulls in core/components, whose Header imports notifications.
import { ExternalOrderStatus } from 'quotes/elements/ExternalOrderStatus';
import { ExternalOrderStatusType, QuoteStatus } from 'quotes/enums';
import { Color, Direction, Level } from 'theme/enums';

// The order is named by its display number (the internal id); rows created before that field
// existed fall back to the id itself.
const getDisplayNumber = (internalOrder: IBellNotificationInternalOrder) =>
  internalOrder.displayNumber || String(internalOrder.id);

// One labelled line per reference the order has: External order, then PO.
const getReferences = (
  internalOrder: IBellNotificationInternalOrder
): IBellNotificationReference[] => [
  ...(internalOrder.externalOrderNumber
    ? [
        {
          key: 'externalOrder',
          label: t('labelBellNotificationExternalOrder'),
          value: internalOrder.externalOrderNumber,
        },
      ]
    : []),
  ...(internalOrder.purchaseOrderNumber
    ? [
        {
          key: 'purchaseOrder',
          label: t('labelBellNotificationPurchaseOrder'),
          value: internalOrder.purchaseOrderNumber,
        },
      ]
    : []),
];

interface IExternalStatusRowProps {
  externalStatus: string;
  label: string;
}

// The chip's own wrapper pads 4px above and below; the item already spaces its rows.
const ExternalStatusRow = ({ externalStatus, label }: IExternalStatusRowProps) => (
  <Stack
    direction={Direction.ROW}
    spacing={0}
    sx={{ alignItems: 'center', gap: 0.75, [`& > .${stackClasses.root}`]: { paddingY: 0 } }}
  >
    <Typography level={Level.BODY_SM} color={Color.NEUTRAL} sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    {/* Any value renders: the chip shows one it does not know as is, in its fallback colour. */}
    <ExternalOrderStatus value={externalStatus as ExternalOrderStatusType} />
  </Stack>
);

// "to" + the chip as one unit that never breaks, so a long title cannot leave "to" alone on a line
// with the chip below it. The chip wrapper's 4px padding would push the title row apart.
const TitleExternalStatus = ({ externalStatus }: { externalStatus: string }) => (
  <Stack
    data-testid="notificationTitleExternalStatus"
    direction={Direction.ROW}
    spacing={0.75}
    sx={{ alignItems: 'center', flexShrink: 0, [`& > .${stackClasses.root}`]: { paddingY: 0 } }}
  >
    <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
      {t('labelBellNotificationExternalStatusChangedTo')}
    </Box>
    <ExternalOrderStatus value={externalStatus as ExternalOrderStatusType} />
  </Stack>
);

const getExternalStatusDetail = (externalStatus: string | null) =>
  externalStatus ? (
    <ExternalStatusRow
      externalStatus={externalStatus}
      label={t('labelNotificationExternalStatusIs')}
    />
  ) : undefined;

const getContent = (notification: IExternalOrderUpdateNotification): IBellNotificationContent => {
  const internalOrder = notification.data?.internalOrder;

  if (!internalOrder) {
    return {
      title: t('titleOrderStatusUpdate'),
      subtitle: null,
      severity: BellNotificationSeverity.INFO,
    };
  }

  const displayNumber = getDisplayNumber(internalOrder);

  if (
    BellNotificationAction.SUBMISSION_FAILED === notification.action ||
    QuoteStatus.ORDER_ORDER_FAILED === internalOrder.status
  ) {
    return {
      title: t('titleBellNotificationOrderSubmissionFailed', { displayNumber }),
      detail: getExternalStatusDetail(internalOrder.externalStatus),
      subtitle: internalOrder.externalOrderErrorMessage || null,
      severity: BellNotificationSeverity.ERROR,
    };
  }

  const references = getReferences(internalOrder);
  const externalStatusDetail = getExternalStatusDetail(internalOrder.externalStatus);

  // A warning, not a failure: the external system never answered, so the order may exist there.
  // Without this branch the title would read "external status changed to" / "was updated".
  if (QuoteStatus.ORDER_SUBMISSION_TIMED_OUT === internalOrder.status) {
    return {
      title: t('titleBellNotificationOrderSubmissionTimedOut', { displayNumber }),
      detail: externalStatusDetail,
      subtitle: null,
      references,
      severity: BellNotificationSeverity.WARNING,
    };
  }

  if (QuoteStatus.ORDER_ORDERING === internalOrder.status) {
    return {
      title: t('titleBellNotificationOrderSubmitting', { displayNumber }),
      detail: externalStatusDetail,
      subtitle: null,
      references,
      severity: BellNotificationSeverity.INFO,
    };
  }

  if (QuoteStatus.ORDER_ORDERED === internalOrder.status) {
    // The transition into ordered; rows created before previous_status existed land here too.
    if (QuoteStatus.ORDER_ORDERED !== internalOrder.previousStatus) {
      return {
        title: t('titleBellNotificationOrderSubmitted', { displayNumber }),
        detail: externalStatusDetail,
        subtitle: null,
        references,
        severity: BellNotificationSeverity.SUCCESS,
      };
    }

    // Nothing changed: DDP delivered the same update again.
    if (
      internalOrder.externalStatus &&
      internalOrder.externalStatus === internalOrder.previousExternalStatus
    ) {
      return {
        title: t('titleBellNotificationOrderUpdateReceived', { displayNumber }),
        detail: externalStatusDetail,
        subtitle: null,
        references,
        severity: BellNotificationSeverity.INFO,
      };
    }
  }

  // The title ends in "changed"; titleEnd carries "to" + the chip.
  if (internalOrder.externalStatus) {
    return {
      title: t('titleBellNotificationExternalOrderStatusChanged', { displayNumber }),
      titleEnd: <TitleExternalStatus externalStatus={internalOrder.externalStatus} />,
      subtitle: null,
      references,
      severity: BellNotificationSeverity.INFO,
    };
  }

  return {
    title: t('titleBellNotificationOrderUpdated', { displayNumber }),
    subtitle: null,
    references,
    severity: BellNotificationSeverity.INFO,
  };
};

const getInternalPath = (notification: IExternalOrderUpdateNotification) =>
  toRouterPath(notification.data?.internalOrder?.internalUrl);

const getActions = (notification: IExternalOrderUpdateNotification): IBellNotificationCta[] => {
  const internalOrder = notification.data?.internalOrder;
  const internalPath = getInternalPath(notification);
  const externalOrderUrl = internalOrder?.externalOrderUrl;

  return [
    ...(internalPath && internalOrder
      ? [
          {
            key: 'order',
            label: t('buttonLabelOrderNumber', { displayNumber: getDisplayNumber(internalOrder) }),
            href: internalPath,
            primary: true,
          },
        ]
      : []),
    ...(isSafeExternalUrl(externalOrderUrl)
      ? [
          {
            key: 'externalOrder',
            label: t('buttonLabelExternalOrder'),
            href: externalOrderUrl,
            external: true,
          },
        ]
      : []),
  ];
};

type TExternalOrderUpdateHandler = IBellNotificationHandler<IExternalOrderUpdateNotification>;

export const externalOrderUpdateNotificationHandler: TExternalOrderUpdateHandler = {
  getActions,
  getContent,
  getInternalPath,
};
